import { ATOMIC_BOMB_JEWEL_ID, type JewelSocketProfile, toExactBigInt } from '@kenocyber/shared';
import type { DamageOrigin } from './origin';
import { damageOriginMayTrigger } from './origin';
import {
  PROC_CHANCE_DENOMINATOR,
  PROC_LEVEL_CAP,
  procChanceNumerator,
  procDamageMilli,
} from './proc-lottery';

/**
 * **O Núcleo Atômico: uma rolagem por SALVA, e no máximo uma bomba nela.**
 *
 * ## Porque isto não é um intervalo da loteria de `proc-lottery.ts`
 *
 * Porque a UNIDADE é outra, e é a única coisa que este gate mudou no modelo do
 * V28G.F. A loteria sorteia uma vez por PROJÉTIL - e escreveu porquê: "uma
 * rolagem por salva daria a um canhão de sete bocas a mesma chance que a um de
 * uma". Para o Ciclone isso está certo, porque o corte é um golpe adicional
 * naquele acerto e um canhão com mais bocas acerta mais vezes.
 *
 * A Bomba não é um golpe adicional. Ela SUBSTITUI um projétil por uma entidade
 * própria com raio, empurrão e área, e uma salva com vinte e oito bombas não
 * seria vinte e oito vezes mais forte - seria outro jogo. Por isso a decisão do
 * dono, no gate V28G.J, é explícita: **a chance é calculada uma vez por salva,
 * independentemente de quantos projéteis o canhão dispara**.
 *
 * Isto REVOGA a linha do V28G.F que punha `jewel_atomic_bomb` a ocupar 18 000
 * do espaço de 300 000 da rolagem por projétil. O que sobrevive intacto é a
 * MATEMÁTICA: o mesmo teto de nível, o mesmo denominador derivado, a mesma
 * fórmula de magnitude com uma única divisão inteira no fim. Este ficheiro não
 * copia nenhuma delas - importa-as - e é isso que faz "outra unidade" não
 * significar "outra aritmética".
 *
 * ## O que sai do espaço da outra loteria, e o que isso custa
 *
 * Nada. O pior conjunto legal de seis somava 210 000 de 300 000 com a Bomba lá
 * dentro; sem ela soma 192 000, e o piso do tiro comum sobe de 30 % para 36 %.
 * A margem só cresce, e é por isso que a saída não obriga a reafinar ninguém.
 *
 * ## Um fluxo de números próprio, e não o do Ciclone
 *
 * Consumir a rolagem da salva do fluxo `hunt-jewel-outgoing-proc-v1` mudaria
 * toda a sequência de procs a partir do primeiro disparo - uma corrida com a
 * mesma semente deixaria de ser a mesma corrida por causa de uma joia que o
 * jogador nem tem. O fluxo é separado, rotulado e consumido SEMPRE, com joia ou
 * sem ela, exatamente pela razão que a loteria já documentou.
 */

/**
 * O teto de chance do Núcleo Atômico: **6 %**, nomeado uma vez.
 *
 * Sai de `docs/gates/v28gf-proc-math.mjs` (`maxChanceBps: 600`) e é o número que
 * produz, através de {@link atomicBombChanceNumerator}, exatamente a tabela que
 * o gate V28G.J declara:
 *
 * | nível | numerador | chance |
 * | --- | --- | --- |
 * | 1 | 600 | 0,2 % |
 * | 6 | 3 600 | 1,2 % |
 * | 15 | 9 000 | 3 % |
 * | 30 | 18 000 | **6 %**, o teto final |
 *
 * Não há linha abaixo do 30. Um nível 31 é recusado por todas as fronteiras que
 * escrevem um perfil, e a aritmética defensiva devolve-lhe o mesmo que ao 30.
 *
 * A chance NUNCA é dividida antes de ser comparada, pela mesma razão de sempre:
 * `600 x 1 / 30` é 20 basis points exatos, mas `1/30` não tem representação
 * decimal finita.
 */
export const ATOMIC_BOMB_MAX_CHANCE_BPS = 600;

/**
 * A fração do orçamento que a Bomba paga em DANO: **75 %**.
 *
 * Os outros 25 % são pagos em CONTROLO - o empurrão de 250 000 unidades de
 * mundo do §6 - e a repartição está declarada em `v28gf-proc-math.mjs`
 * (`damageShare: 0.75`) em vez de esquecida. É ela que faz a magnitude ser
 * 112,5 % do dano-base em vez dos 150 % que uma joia de dano puro receberia com
 * este teto de chance.
 *
 * Em basis points e `bigint` porque entra numa multiplicação exata: um `0.75`
 * em vírgula flutuante seria um float no caminho do dano, e não há nenhum.
 */
export const ATOMIC_BOMB_DAMAGE_SHARE_BPS = 7500n;

/** Os rótulos dos fluxos da rolagem por SALVA. Versionados como os da loteria. */
export const PVP_VOLLEY_BOMB_RNG_LABEL = 'pvp-jewel-volley-bomb-v1';
export const HUNT_VOLLEY_BOMB_RNG_LABEL = 'hunt-jewel-volley-bomb-v1';
/**
 * O gémeo do MUNDO ABERTO, com a mesma finalidade e a mesma unidade.
 *
 * Um rótulo próprio, e não uma reutilização: um rótulo é o que separa duas
 * sequências, e "de que modo é este fluxo" tem de continuar a ser legível numa
 * linha de import. A cadência e a razão de este fluxo ser separado dos outros
 * são as mesmas de sempre.
 */
export const OPEN_WORLD_VOLLEY_BOMB_RNG_LABEL = 'open-world-jewel-volley-bomb-v1';

/**
 * O numerador desta joia, dado o seu nível somado.
 *
 * Inteiro, nunca dividido, e comparado contra {@link PROC_CHANCE_DENOMINATOR} -
 * o MESMO espaço de rolagem da outra loteria, porque a lei de chance é a mesma
 * lei. Acima de {@link PROC_LEVEL_CAP} devolve sempre o mesmo valor.
 */
export function atomicBombChanceNumerator(levelSum: number): number {
  return procChanceNumerator(ATOMIC_BOMB_MAX_CHANCE_BPS, levelSum);
}

/**
 * **O dano adicional da explosão, em milli, congelado quando a salva nasceu.**
 *
 *     dano = base x 30 x min(nível, 30) x 30 x 7500
 *            -------------------------------------
 *              600 x min(nível, 30) x 10000
 *
 * Uma única divisão inteira, no fim, e a função é a da loteria - não uma cópia
 * dela. A magnitude é **112,5 %** do dano-base em qualquer nível: os dois lados
 * da fração crescem juntos até 30 e congelam juntos depois. O que varia com o
 * nível é a CHANCE, e é isso que mantém o poder esperado linear.
 *
 * Chegou a valer 675 % em `levelSum` 180, quando seis cópias da mesma joia eram
 * legais e só o denominador congelava. O V28G.K aboliu essa configuração, e com
 * ela a metade da curva que a descrevia.
 *
 * `baseMilli` é o dano-base AUTORITATIVO da nave e mais nada: sem a Chama, sem
 * os Corações, sem runas, sem crítico e sem outro proc.
 */
export function atomicBombBlastMilli(baseMilli: bigint, levelSum: bigint): bigint {
  return procDamageMilli(
    baseMilli,
    ATOMIC_BOMB_MAX_CHANCE_BPS,
    levelSum,
    ATOMIC_BOMB_DAMAGE_SHARE_BPS,
  );
}

/**
 * A bomba que uma salva carrega, congelada no nascimento.
 *
 * `blastMilli` é o RESULTADO e nunca a receita, pela mesma lei que congela
 * `damageMilli` e o `cutMilli` do Ciclone: guardar `levelSum` obrigaria a
 * reavaliar a fórmula na detonação, e uma reavaliação responde "quanto vale
 * agora" em vez de "quanto valia quando esta salva saiu".
 *
 * `entropy` viaja com ela para dar determinismo às decisões internas que a
 * explosão precisa de tomar - a direção de um empurrão sobre um casco que está
 * exatamente no centro - sem uma segunda rolagem na detonação, que seria uma
 * rolagem que a instantânea teria de preservar à parte.
 *
 * Nunca carrega conta, skin, socket, nível ou contagem de cópias: a bomba
 * atravessa o fio para seis clientes.
 */
export interface AtomicBombSelection {
  readonly entropy: number;
  readonly blastMilli: bigint;
}

/** A bomba como um DOCUMENTO a guarda: `bigint` não existe em JSON. */
export interface AtomicBombSelectionWire {
  readonly entropy: number;
  readonly blastMilli: string;
}

export function atomicBombToWire(
  selection: AtomicBombSelection | null,
): AtomicBombSelectionWire | null {
  return selection === null
    ? null
    : { entropy: selection.entropy, blastMilli: selection.blastMilli.toString(10) };
}

/** Do documento para o vivo, e **sem reavaliar coisa nenhuma**. */
export function atomicBombFromWire(
  wire: AtomicBombSelectionWire | null,
): AtomicBombSelection | null {
  return wire === null
    ? null
    : Object.freeze({ entropy: wire.entropy, blastMilli: BigInt(wire.blastMilli) });
}

/**
 * **A rolagem da salva.** Uma por salva, com joia ou sem ela.
 *
 * `roll` tem de vir de `[0, PROC_CHANCE_DENOMINATOR)` e ser produzido pelo fluxo
 * autoritativo do participante. Devolve `null` quando a rolagem cai fora do
 * numerador, quando a nave não tem a joia, e quando a origem não pode originar
 * joia nenhuma - as três razões são diferentes e nenhuma produz bomba.
 */
export function selectVolleyBomb(
  profile: JewelSocketProfile,
  origin: DamageOrigin,
  roll: number,
  entropy: number,
  baseMilli: bigint,
): AtomicBombSelection | null {
  /*
   * **A tabela fechada, nas suas DUAS metades** (gate V28G.P).
   *
   * É aqui que a exclusão do contrato V28G.P deixa de depender de não existir
   * chamador: uma serra NÃO pode lançar um míssil nem largar um Núcleo Atômico,
   * e a razão é estrutural - as duas são sorteadas uma vez por SALVA, e uma
   * serra não dispara salva nenhuma. A linha abaixo diz o mesmo uma segunda
   * vez, para que escrever o chamador que nao existe continue a nao produzir
   * nada.
   */
  if (!damageOriginMayTrigger(origin, 'atomic_blast')) {
    return null;
  }
  if (!Number.isInteger(roll) || roll < 0 || roll >= PROC_CHANCE_DENOMINATOR) {
    throw new RangeError(`volley bomb roll outside [0, ${PROC_CHANCE_DENOMINATOR}): ${roll}`);
  }
  if (typeof baseMilli !== 'bigint' || baseMilli < 0n) {
    throw new RangeError(`a blast base is a non-negative bigint, received ${String(baseMilli)}`);
  }
  const owned = profile.entries.find((entry) => entry.id === ATOMIC_BOMB_JEWEL_ID);
  if (!owned) {
    return null;
  }
  const levelSum = toExactBigInt(owned.levelSum, `${owned.id} levelSum`);
  if (levelSum <= 0n) {
    return null;
  }
  if (roll >= atomicBombChanceNumerator(Number(levelSum))) {
    return null;
  }
  return Object.freeze({
    entropy: entropy >>> 0,
    blastMilli: atomicBombBlastMilli(baseMilli, levelSum),
  });
}

/**
 * **Qual projétil da salva se torna a bomba: o mais próximo do eixo central.**
 *
 * ## Porque a entrada são deslocamentos e não um índice
 *
 * Porque "o eixo central da salva" não é um índice em modo nenhum. Na caça uma
 * salva sai de vários canhões, cada um com o seu próprio leque, e o projétil
 * central de UM canhão pode estar longe do eixo da nave. O que decide é a
 * distância ao eixo, e quem a sabe medir é a simulação que colocou cada
 * projétil - portanto ela mede, e esta função escolhe.
 *
 * ## O desempate
 *
 * Menor índice. Uma salva com um número par de projéteis tem dois exatamente
 * empatados a `+/- espaçamento/2`, e escolher "o primeiro" é determinístico,
 * estável e independente da ordem por que a simulação os criou - porque essa
 * ordem é ela própria canónica: canhão a canhão, boca a boca.
 *
 * Nunca `Math.random()`, nunca a entropia, nunca o relógio.
 */
export function centralVolleyIndex(lateralOffsets: readonly number[]): number {
  const first = lateralOffsets[0];
  if (first === undefined) {
    throw new RangeError('a volley with no projectiles has no centre');
  }
  let best = 0;
  let bestDistance = Math.abs(first);
  for (let index = 1; index < lateralOffsets.length; index += 1) {
    const distance = Math.abs(lateralOffsets[index] as number);
    if (distance < bestDistance) {
      best = index;
      bestDistance = distance;
    }
  }
  return best;
}
